import { Injectable, Injector } from '@angular/core';
import { Observable } from 'rxjs';
import { ActionCableChannel } from '../models/action-cable.model';
import {
  RepairMessageText,
  RepairMessageTextParams,
} from '../models/repair-message-text.model';
import {
  RepairMessage,
  RepairMessageAreaOption,
  RepairMessageCategory,
  RepairMessageParams,
  RepairMessageQueryParams,
  RepairMessageResponse,
  RepairMessagesByDate,
  WS_RepairMessage,
  RepairMessagesFilters,
  TicketType,
} from '../models/repair-messages';
import { toHttpParams } from '../utils/query-params';
import {
  ActionCableService,
  Connected,
  Disconnected,
} from './action-cable.service';
import { ApiService } from './api.service';

export type RepairMessageWSEvent = Connected | Disconnected | WS_RepairMessage;

@Injectable({
  providedIn: 'root',
})
export class RepairMessagesService extends ApiService {
  constructor(
    injector: Injector,
    private actionCableRepository: ActionCableService
  ) {
    super(injector);
  }

  update(repairMessage: Partial<RepairMessage>): Observable<RepairMessage> {
    return this.http.put<RepairMessage>(
      `${this.API_URL}/repair_messages/${repairMessage.id}`,
      {
        repair_message: repairMessage,
      }
    );
  }

  create(repairMessage: RepairMessageParams): Observable<RepairMessage> {
    return this.http.post<RepairMessage>(`${this.API_URL}/repair_messages`, {
      repair_message: repairMessage,
    });
  }

  findAll(params: RepairMessageQueryParams): Observable<RepairMessageResponse> {
    return this.http.get<RepairMessageResponse>(
      `${this.API_URL}/repair_messages`,
      {
        params: toHttpParams(params),
      }
    );
  }

  getExportData(): Observable<any> {
    return this.http.get(
      `${this.API_URL}/repair_messages/export_repair_messages`,
      { responseType: 'text' }
    );
  }

  getExportSingleTicketData(repairMessageId: number): Observable<any> {
    return this.http.get(
      `${this.API_URL}/repair_messages/export_single_ticket`,
      { params: { id: repairMessageId }, responseType: 'blob' }
    );
  }

  getAvailableFilters(): Observable<RepairMessagesFilters> {
    return this.http.get<RepairMessagesFilters>(
      `${this.API_URL}/repair_messages/find_all_filters`
    );
  }

  getDates(): Observable<RepairMessagesByDate[]> {
    return this.http.get<RepairMessagesByDate[]>(
      `${this.API_URL}/repair_messages/dates`
    );
  }

  getRepairMessage(id: number | string): Observable<RepairMessage> {
    return this.http.get<RepairMessage>(
      `${this.API_URL}/repair_messages/${id}`
    );
  }

  forwardToAmacos(id: number): Observable<RepairMessage> {
    return this.http.post<RepairMessage>(
      `${this.API_URL}/repair_messages/${id}/forward_to_amacos`,
      {}
    );
  }

  postReplyMessage(
    repairMessageText: RepairMessageTextParams
  ): Observable<RepairMessageText> {
    return this.http.post<RepairMessageText>(
      this.API_URL + '/repair_message_texts',
      {
        repair_message_text: repairMessageText,
      }
    );
  }

  getRepairMessageAreas(): Observable<RepairMessageAreaOption[]> {
    return this.http.get<RepairMessageAreaOption[]>(
      `${this.BASE_URL}/repair_message_categories/areas`
    );
  }

  getRepairMessageCategories(
    ticketType: TicketType
  ): Observable<RepairMessageCategory[]> {
    return this.http.get<RepairMessageCategory[]>(
      `${this.BASE_URL}/repair_message_categories`,
      {
        params: { ticket_type: ticketType },
      }
    );
  }

  repairMessageWSEvents(
    repairMessageId: number
  ): Observable<RepairMessageWSEvent> {
    return this.actionCableRepository.subscribeToChannel(
      ActionCableChannel.RepairMessageChannel,
      { repair_message_id: repairMessageId }
    );
  }
}
